import {Center} from '@resurs-internal/mui';
import useAuthState from '../../hooks/useAuthState';
import {useQueryClient} from '@tanstack/react-query';
import {persistQueryClientSave} from '@tanstack/react-query-persist-client';
import {persister} from 'App';
import {ResursLoading} from 'components';
import React, {useEffect, useMemo, useState} from 'react';
import {config} from '../../config';
import {
  DemoLoginCountry,
  demoLoginCountries,
  toDemoLoginCountry,
} from './demoLoginCountry';

let isPersisting = false;
let hasRedirected = false;

const cachedAuthRedirectUrls: Partial<Record<DemoLoginCountry, string>> = {};

export const LoginDemo: React.FC = () => {
  const queryClient = useQueryClient();
  const [isPersisted, setIsPersisted] = useState(false);
  const auth = useAuthState();

  const authRedirectUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const country = toDemoLoginCountry(params.get('country'));
    const cached = cachedAuthRedirectUrls[country];
    if (cached) return cached;
    const redirect = params.get('redirect');
    if (redirect) sessionStorage.setItem('redirect', redirect);
    const {scope, language} = demoLoginCountries[country];
    const url = auth.createAuthRedirectURL({
      redirectUri: `${config.clientHost}/demo-auth`,
      scope,
      countryCode: country,
      language,
    });
    // createAuthRedirectURL has an unavoidable side effect (it writes a fresh
    // OAuth2 state/codeVerifier to the auth store). Caching the result here,
    // synchronously, is what makes the cachedAuthRedirectUrls guard above work
    // under React.StrictMode's double-invoke: without it, the second
    // invocation wouldn't see the cache yet and would generate a second,
    // different state - one embedded in this URL, a different one later
    // persisted to sessionStorage - causing auth_state_invalid on callback.
    // Moving this into a useEffect (as a past purity-lint fix did) reintroduces
    // that exact race, since effects run after both invocations complete.
    cachedAuthRedirectUrls[country] = url; // eslint-disable-line react-hooks/globals, react-hooks/immutability
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // redirect when authRedirectUrl and state is set
  useEffect(() => {
    const persistAuthState = async () => {
      isPersisting = true;
      await persistQueryClientSave({
        queryClient,
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: (qry) => qry.queryKey.join('') === 'auth',
        },
      });
      // Necessary until the await above is fixed in react-query-persist-client.
      // It looks like even direct calls to persist save are throttled
      // to occur no more than once per second.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsPersisted(true);
      isPersisting = false;
    };

    if (!authRedirectUrl.length || !auth.state.stateId) {
      return;
    }

    if (auth.state.codeVerifier != null) {
      sessionStorage.setItem('codeVerifier', auth.state.codeVerifier);
    }
    sessionStorage.setItem('state', auth.state.stateId);

    if (!isPersisting && !isPersisted) {
      persistAuthState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.state.stateId, authRedirectUrl.length, isPersisted, queryClient]);

  // Navigate once, from an effect. Calling replace() in render ran it twice under StrictMode,
  // and each call reached oba-auth-service as its own /auth, which creates its own DIDS
  // authentication. The cachedAuthRedirectUrls guard above only makes both calls use the same
  // state; this one stops the second request.
  useEffect(() => {
    if (!authRedirectUrl || !isPersisted || hasRedirected) {
      return;
    }
    hasRedirected = true;
    window.location.replace(authRedirectUrl);
  }, [authRedirectUrl, isPersisted]);

  return (
    <Center>
      <ResursLoading />
    </Center>
  );
};
